import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { toast } from 'react-toastify'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { IInventoryCategoriesSlice, ISingleInventoryCategory } from 'types/inventory.interface'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IInventoryCategoryPatchRequest,
  IInventoryCategoryResponse,
} from 'types/api/categoriesApi.interface'
import { IInventoryCategoryModal } from 'pages/Inventory/Settings/InventoryCategoryModal'
import { IInvenotryCategoryPostRequest } from 'types/api/inventoryItems.interface'

const initialState: IInventoryCategoriesSlice = {
  isLoading: false,
  inventoryCategories: [],
  singleInventoryCategory: null,
  totalCount: 0,
  totalPages: 0,
}

export const getInventoryCategories = createAsyncThunk(
  'inventoryCategories/getInventoryCategories',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryCategoryResponse>> = await baseUrl.get(
        'inventory/category',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addInventoryCategory = createAsyncThunk(
  'inventoryCategories/addInventoryCategory',
  async (
    { category, parentId }: { category: IInventoryCategoryModal; parentId: number },
    thunkAPI,
  ) => {
    try {
      const forSubmit: IInvenotryCategoryPostRequest = {
        name: category.name,
        parentId,
        defaultCostingMethod: category.defaultCostingMethod?.value,
        defaultTrackingMode: category.defaultTrackingMode?.value,
      }
      const resp = await baseUrl.post('inventory/category', forSubmit, thunkAPI)
      if (parentId) {
        await thunkAPI.dispatch(getSingleInventoryCategory(parentId))
      } else {
        await thunkAPI.dispatch(getInventoryCategories(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleInventoryCategory = createAsyncThunk(
  'inventoryCategories/getSingleInventoryCategory',
  async (id: number | string, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISingleInventoryCategory> = await baseUrl.get(
        'inventory/category/' + id,
        thunkAPI,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateInventoryCategory = createAsyncThunk(
  'inventoryCategories/updateInventoryCategory',
  async (
    { category, fields }: { category: IInventoryCategoryModal; fields?: number[] },
    thunkAPI,
  ) => {
    try {
      const req: IInventoryCategoryPatchRequest = {
        name: category.name,
        defaultTrackingMode: category.defaultTrackingMode?.value,
        defaultCostingMethod: category.defaultCostingMethod?.value,
        fieldIds: fields,
      }
      const resp = await baseUrl.patch('inventory/category/' + category.id, req, thunkAPI)
      await thunkAPI.dispatch(getSingleInventoryCategory(category.id))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteInventoryCategory = createAsyncThunk(
  'inventoryCategories/deleteInventoryCategory',
  async ({ id, reloadId }: { id: number; reloadId?: number }, thunkAPI) => {
    try {
      await baseUrl.delete('inventory/category/' + id, thunkAPI)
      if (reloadId) {
        await thunkAPI.dispatch(getSingleInventoryCategory(reloadId))
      } else {
        await thunkAPI.dispatch(getInventoryCategories(getParams({ location })))
      }
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const inventoryCategoriesSlice = createSlice({
  name: 'inventoryItems',
  initialState,
  reducers: {
    clearInventoryCategory: (state: any) => {
      state.singleInventoryCategory = {}
    },
    clearInventoryCategoriesList: (state: any) => {
      state.inventoryCategories = []
      state.totalCount = 0
      state.totalPages = 0
    },
  },
  extraReducers: {
    [addInventoryCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [addInventoryCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Category added')
    },
    [addInventoryCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryCategories.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryCategories.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.inventoryCategories = payload.data
      state.totalPages = payload.totalPages
      state.totalCount = payload.totalCount
    },
    [getInventoryCategories.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getSingleInventoryCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleInventoryCategory.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleInventoryCategory = payload
    },
    [getSingleInventoryCategory.rejected.type]: (state) => {
      state.isLoading = false
    },
    [updateInventoryCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateInventoryCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Category updated')
    },
    [updateInventoryCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteInventoryCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteInventoryCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Category deleted')
    },
    [deleteInventoryCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})
export const { clearInventoryCategory, clearInventoryCategoriesList } =
  inventoryCategoriesSlice.actions

export default inventoryCategoriesSlice.reducer
